import axios from 'axios';
import {
  ProvidersResponse,
  ConversationMetrics,
  AgentTemplate,
  CatalogTemplate,
  Agent,
  Workspace,
  ConnectResponse,
  AgentPublicInfo,
  SessionSummary,
  SessionMode,
  TextChatResult,
} from '../types';

export const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:4001/api';

const api = axios.create({
  baseURL: API_BASE_URL,
});

// ── Auth ──

export function setAuthToken(token: string | null) {
  if (token) {
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  } else {
    delete api.defaults.headers.common['Authorization'];
  }
}

export async function login(username: string, password: string): Promise<{ token: string }> {
  const { data } = await api.post<{ token: string }>('/auth/login', { username, password });
  return data;
}

export async function register(username: string, password: string): Promise<{ token: string }> {
  const { data } = await api.post<{ token: string }>('/auth/register', { username, password });
  return data;
}

export async function checkSetupRequired(): Promise<boolean> {
  const { data } = await api.get<{ setupRequired: boolean }>('/auth/setup-required');
  return data.setupRequired;
}

export async function checkAuth(): Promise<{ ok: boolean; id?: string; username?: string }> {
  try {
    const { data } = await api.get<{ ok: boolean; id: string; username: string }>('/auth/me');
    return data;
  } catch {
    return { ok: false };
  }
}

// ── Users (admin) ──

export interface UserInfo {
  id: string;
  username: string;
  createdAt: string;
}

export async function listUsers(): Promise<UserInfo[]> {
  const { data } = await api.get<UserInfo[]>('/auth/users');
  return data;
}

export async function createUser(username: string, password: string): Promise<UserInfo> {
  const { data } = await api.post<UserInfo>('/auth/users', { username, password });
  return data;
}

export async function updateUser(id: string, updates: { username?: string; password?: string }): Promise<UserInfo> {
  const { data } = await api.put<UserInfo>(`/auth/users/${id}`, updates);
  return data;
}

export async function deleteUser(id: string): Promise<void> {
  await api.delete(`/auth/users/${id}`);
}

// ── Config ──

export interface PublicConfig {
  twilioBaseUrl: string;
  backendUrl: string;
}

export async function getPublicConfig(): Promise<PublicConfig> {
  const { data } = await api.get<PublicConfig>('/config/public');
  return data;
}

// ── Providers ──

export async function fetchProviders(): Promise<ProvidersResponse> {
  const { data } = await api.get<ProvidersResponse>('/providers');
  return data;
}

// ── Compare mode ──

export interface ProcessTextRequest {
  text: string;
  sttProvider: string;
  ttsProvider: string;
  language?: string;
  referenceText?: string;
}

export interface ProcessAudioRequest {
  audio: Blob;
  sttProvider: string;
  ttsProvider: string;
  language?: string;
  referenceText?: string;
}

export interface ProcessResult {
  transcribedText: string;
  llmResponseText: string;
  audio?: Blob;
  metrics: ConversationMetrics;
}

export async function processAudio(req: ProcessAudioRequest): Promise<ProcessResult> {
  const formData = new FormData();
  formData.append('audio', req.audio, 'recording.webm');
  formData.append('sttProvider', req.sttProvider);
  formData.append('ttsProvider', req.ttsProvider);
  if (req.language) formData.append('language', req.language);
  if (req.referenceText) formData.append('referenceText', req.referenceText);

  const response = await api.post('/conversation/process', formData, {
    responseType: req.ttsProvider !== 'browserTts' ? 'arraybuffer' : 'json',
  });

  if (req.ttsProvider !== 'browserTts') {
    const headers = response.headers;
    return {
      transcribedText: decodeURIComponent(headers['x-transcribed-text'] || ''),
      llmResponseText: decodeURIComponent(headers['x-llm-response-text'] || ''),
      audio: new Blob([response.data], { type: headers['content-type'] }),
      metrics: JSON.parse(decodeURIComponent(headers['x-metrics'] || '{}')),
    };
  }

  return response.data as ProcessResult;
}

export async function processText(req: ProcessTextRequest): Promise<ProcessResult> {
  const formData = new FormData();
  formData.append('text', req.text);
  formData.append('sttProvider', req.sttProvider);
  formData.append('ttsProvider', req.ttsProvider);
  if (req.referenceText) formData.append('referenceText', req.referenceText);

  const response = await api.post('/conversation/process', formData, {
    responseType: req.ttsProvider !== 'browserTts' ? 'arraybuffer' : 'json',
  });

  if (req.ttsProvider !== 'browserTts') {
    const headers = response.headers;
    return {
      transcribedText: decodeURIComponent(headers['x-transcribed-text'] || ''),
      llmResponseText: decodeURIComponent(headers['x-llm-response-text'] || ''),
      audio: new Blob([response.data], { type: headers['content-type'] }),
      metrics: JSON.parse(decodeURIComponent(headers['x-metrics'] || '{}')),
    };
  }

  return response.data as ProcessResult;
}

// ── Templates (admin) ──

export interface CreateTemplateRequest {
  name: string;
  description?: string;
  greeting?: string;
  sttProvider: string;
  ttsProvider: string;
  llmModel: string;
  baseSystemPrompt: string;
  language?: string;
  voiceMode?: 'pipeline' | 'realtime';
  customFields?: AgentTemplate['customFields'];
  isPublic?: boolean;
}

export async function createTemplate(data: CreateTemplateRequest): Promise<AgentTemplate> {
  const { data: template } = await api.post<AgentTemplate>('/templates', data);
  return template;
}

export async function listTemplates(): Promise<AgentTemplate[]> {
  const { data } = await api.get<AgentTemplate[]>('/templates');
  return data;
}

export async function getTemplate(id: string): Promise<AgentTemplate> {
  const { data } = await api.get<AgentTemplate>(`/templates/${id}`);
  return data;
}

export async function updateTemplate(id: string, updates: Partial<CreateTemplateRequest>): Promise<AgentTemplate> {
  const { data: template } = await api.put<AgentTemplate>(`/templates/${id}`, updates);
  return template;
}

export async function deleteTemplate(id: string): Promise<void> {
  await api.delete(`/templates/${id}`);
}

export interface McpTestResult {
  serverCount: number;
  toolCount: number;
  tools: { name: string; description: string; serverName: string }[];
}

export async function testAgentMcp(agentId: string): Promise<McpTestResult> {
  const { data } = await api.post<McpTestResult>(`/agents/${agentId}/mcp-test`);
  return data;
}

// ── Workspaces (admin) ──

export async function createWorkspace(data: { name: string; templateIds?: string[] }): Promise<Workspace> {
  const { data: workspace } = await api.post<Workspace>('/workspaces', data);
  return workspace;
}

export async function listWorkspaces(): Promise<Workspace[]> {
  const { data } = await api.get<Workspace[]>('/workspaces');
  return data;
}

export async function getWorkspace(id: string): Promise<Workspace> {
  const { data } = await api.get<Workspace>(`/workspaces/${id}`);
  return data;
}

export async function updateWorkspace(id: string, updates: { name?: string; templateIds?: string[] }): Promise<Workspace> {
  const { data: workspace } = await api.put<Workspace>(`/workspaces/${id}`, updates);
  return workspace;
}

export async function deleteWorkspace(id: string): Promise<void> {
  await api.delete(`/workspaces/${id}`);
}

// ── Catalog (public) ──

export async function listCatalogTemplates(): Promise<CatalogTemplate[]> {
  const { data } = await api.get<CatalogTemplate[]>('/catalog/templates');
  return data;
}

export async function getCatalogTemplate(id: string): Promise<CatalogTemplate> {
  const { data } = await api.get<CatalogTemplate>(`/catalog/templates/${id}`);
  return data;
}

// ── Agents (user) ──

export interface CreateAgentRequest {
  templateId: string;
  name: string;
  customPrompt?: string;
  customFieldValues?: Record<string, string>;
  mcpServers?: Agent['mcpServers'];
  maxHistoryMessages?: number;
  greeting?: string;
  detailedMetrics?: boolean;
  // null clears the override so the agent inherits the template's voice mode.
  voiceMode?: Agent['voiceMode'];
  knowledgeBaseIds?: string[];
  whatsapp?: Agent['whatsapp'];
  eazyshow?: Agent['eazyshow'];
  mediaUpload?: Agent['mediaUpload'];
}

export async function createAgent(data: CreateAgentRequest): Promise<Agent> {
  const { data: agent } = await api.post<Agent>('/agents', data);
  return agent;
}

export async function listAgents(): Promise<Agent[]> {
  const { data } = await api.get<Agent[]>('/agents');
  return data;
}

export async function getAgent(id: string): Promise<Agent> {
  const { data } = await api.get<Agent>(`/agents/${id}`);
  return data;
}

export async function updateAgent(id: string, updates: Partial<CreateAgentRequest>): Promise<Agent> {
  const { data: agent } = await api.put<Agent>(`/agents/${id}`, updates);
  return agent;
}

export async function deleteAgent(id: string): Promise<void> {
  await api.delete(`/agents/${id}`);
}

export async function getAgentInfo(apiKey: string): Promise<AgentPublicInfo> {
  const { data } = await api.post<AgentPublicInfo>('/agents/info', {}, {
    headers: { 'x-agent-key': apiKey },
  });
  return data;
}

export type ResumedConversation = {
  sessionId: string;
  /** 'session' = platform session; 'thread' = a /api/std chat a session was just attached to. */
  kind: 'session' | 'thread';
  status: 'active' | 'ended';
  messages: { role: 'user' | 'assistant'; text: string; timestamp: string }[];
};

/**
 * Resume a conversation by id — a platform session id or a Mastra thread id
 * (a chat started via /api/std). Validates ownership against the agent key and
 * returns the transcript; for a thread a platform session is attached to it.
 */
export async function resumeConversation(
  apiKey: string,
  id: string,
): Promise<ResumedConversation> {
  const { data } = await api.post<ResumedConversation>(
    '/agents/resume',
    { id },
    { headers: { 'x-agent-key': apiKey } },
  );
  return data;
}

export async function connectToAgent(
  apiKey: string,
  mode?: SessionMode,
): Promise<ConnectResponse> {
  const { data } = await api.post<ConnectResponse>(
    '/agents/connect',
    mode ? { mode } : {},
    { headers: { 'x-agent-key': apiKey } },
  );
  return data;
}

export async function disconnectAgent(
  apiKey: string,
  sessionId: string,
): Promise<void> {
  await api.post(
    '/agents/disconnect',
    {},
    { headers: { 'x-agent-key': apiKey, 'x-session-id': sessionId } },
  );
}

export async function sendTextChat(
  apiKey: string,
  sessionId: string,
  message: string,
): Promise<TextChatResult> {
  const { data } = await api.post<TextChatResult>(
    '/conversation/text-chat',
    { message },
    { headers: { 'x-agent-key': apiKey, 'x-session-id': sessionId } },
  );
  return data;
}

// ── Sessions ──

export async function listAgentSessions(agentId: string): Promise<SessionSummary[]> {
  const { data } = await api.get<SessionSummary[]>(`/agents/${agentId}/sessions`);
  return data;
}

export async function listAllSessions(): Promise<SessionSummary[]> {
  const { data } = await api.get<SessionSummary[]>('/agents/all-sessions');
  return data;
}

// ── Mastra Memory conversations (threads from the /api/std drop-in) ──

export interface MastraThread {
  id: string;
  title?: string;
  createdAt?: string;
  updatedAt?: string;
}
export interface MastraThreadMessage {
  role: string;
  text: string;
  createdAt?: string;
}

export async function listAgentThreads(agentId: string): Promise<MastraThread[]> {
  const { data } = await api.get<MastraThread[]>(`/agents/${agentId}/threads`);
  return data;
}

export async function getAgentThread(
  agentId: string,
  threadId: string,
): Promise<{ threadId: string; messages: MastraThreadMessage[] }> {
  const { data } = await api.get(`/agents/${agentId}/threads/${threadId}`);
  return data;
}

// ── Std request log (observability of /api/std generate/stream calls) ──

export interface StdRequestLog {
  id: string;
  createdAt?: string;
  agentId?: string;
  threadId?: string;
  resourceId?: string;
  stream: boolean;
  status?: number;
  system?: string;
  messages?: { role: string; content: string }[];
  response?: string;
  usage?: unknown;
}

export async function listAgentStdRequests(agentId: string): Promise<StdRequestLog[]> {
  const { data } = await api.get<StdRequestLog[]>(`/agents/${agentId}/std-requests`);
  return data;
}

export async function listAllStdRequests(agentIds: string[]): Promise<StdRequestLog[]> {
  const perAgent = await Promise.all(
    agentIds.map((agentId) =>
      listAgentStdRequests(agentId).catch(() => [] as StdRequestLog[]),
    ),
  );
  return perAgent
    .flat()
    .sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''));
}

// Cross-agent: fan out per-agent thread lists (threads are scoped by agent id).
export interface MastraThreadWithAgent extends MastraThread {
  agentId: string;
}
export async function listAllThreads(
  agentIds: string[],
): Promise<MastraThreadWithAgent[]> {
  const perAgent = await Promise.all(
    agentIds.map((agentId) =>
      listAgentThreads(agentId)
        .then((ts) => ts.map((t) => ({ ...t, agentId })))
        .catch(() => [] as MastraThreadWithAgent[]),
    ),
  );
  return perAgent
    .flat()
    .sort((a, b) => (b.updatedAt || '').localeCompare(a.updatedAt || ''));
}

// ── Agent process (voice chat) ──

export async function processAgentAudio(
  apiKey: string,
  sessionId: string,
  audio: Blob,
): Promise<ProcessResult> {
  const formData = new FormData();
  formData.append('audio', audio, 'recording.webm');

  const response = await api.post('/conversation/agent-process', formData, {
    headers: { 'x-agent-key': apiKey, 'x-session-id': sessionId },
    responseType: 'arraybuffer',
  });

  const headers = response.headers;
  const contentType = headers['content-type'] || '';

  if (contentType.startsWith('audio/')) {
    return {
      transcribedText: decodeURIComponent(headers['x-transcribed-text'] || ''),
      llmResponseText: decodeURIComponent(headers['x-llm-response-text'] || ''),
      audio: new Blob([response.data], { type: contentType }),
      metrics: JSON.parse(decodeURIComponent(headers['x-metrics'] || '{}')),
    };
  }

  const text = new TextDecoder().decode(response.data);
  return JSON.parse(text) as ProcessResult;
}

export async function processAgentText(
  apiKey: string,
  sessionId: string,
  text: string,
): Promise<ProcessResult> {
  const formData = new FormData();
  formData.append('text', text);

  const response = await api.post('/conversation/agent-process', formData, {
    headers: { 'x-agent-key': apiKey, 'x-session-id': sessionId },
    responseType: 'arraybuffer',
  });

  const headers = response.headers;
  const contentType = headers['content-type'] || '';

  if (contentType.startsWith('audio/')) {
    return {
      transcribedText: decodeURIComponent(headers['x-transcribed-text'] || ''),
      llmResponseText: decodeURIComponent(headers['x-llm-response-text'] || ''),
      audio: new Blob([response.data], { type: contentType }),
      metrics: JSON.parse(decodeURIComponent(headers['x-metrics'] || '{}')),
    };
  }

  const decoded = new TextDecoder().decode(response.data);
  return JSON.parse(decoded) as ProcessResult;
}

// ── Knowledge bases (global, reusable; attach to agents via knowledgeBaseIds) ──

export interface KnowledgeBase {
  id: string;
  name: string;
  description?: string;
  createdAt: string;
  docCount: number;
}

export interface KnowledgeDoc {
  id: string;
  kbId: string;
  filename: string;
  sizeBytes: number;
  chunks: number;
  createdAt: string;
}

export async function listKnowledgeBases(): Promise<KnowledgeBase[]> {
  const { data } = await api.get<{ bases: KnowledgeBase[] }>('/knowledge-bases');
  return data.bases;
}

export async function createKnowledgeBase(name: string, description?: string): Promise<KnowledgeBase> {
  const { data } = await api.post<KnowledgeBase>('/knowledge-bases', { name, description });
  return { ...data, docCount: 0 };
}

export async function renameKnowledgeBase(kbId: string, name: string): Promise<KnowledgeBase> {
  const { data } = await api.patch<KnowledgeBase>(`/knowledge-bases/${kbId}`, { name });
  return data;
}

export async function deleteKnowledgeBase(kbId: string): Promise<void> {
  await api.delete(`/knowledge-bases/${kbId}`);
}

export async function listKnowledgeDocs(kbId: string): Promise<KnowledgeDoc[]> {
  const { data } = await api.get<{ docs: KnowledgeDoc[] }>(`/knowledge-bases/${kbId}/docs`);
  return data.docs;
}

export async function uploadKnowledgeFile(kbId: string, file: File): Promise<KnowledgeDoc> {
  const form = new FormData();
  form.append('file', file);
  const { data } = await api.post<KnowledgeDoc>(`/knowledge-bases/${kbId}/docs`, form);
  return data;
}

export async function deleteKnowledgeDoc(kbId: string, docId: string): Promise<void> {
  await api.delete(`/knowledge-bases/${kbId}/docs/${docId}`);
}
